import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryParams } from 'hooks/useQueryParams';
import {
  TIMELINE_LAYER_IDS,
  TimelineLayerId,
} from 'features/mapping/mapping.constants';
import { useTimelineLayerList } from 'features/mapping/hooks/useTimelineLayerList';
import { MAP_MODE_PARAM_KEY } from './useMapMode.constants';

// In timeline mode the value is the timeline layer shown, e.g. `?mapMode=radar`
export type MapMode = 'default' | TimelineLayerId;

type MapModeParams = { [MAP_MODE_PARAM_KEY]?: string };

export type SetMapModeOptions = {
  // Dropped in the same navigation
  removeParams?: readonly string[];
};

type UseMapModeReturn = {
  mapMode: MapMode;
  isTimelineMode: boolean;
  setMapMode: (mode: MapMode, options?: SetMapModeOptions) => void;
};

export const useMapMode = (): UseMapModeReturn => {
  const history = useHistory();
  const location = useLocation();
  const [params] = useQueryParams<MapModeParams>();
  const layers = useTimelineLayerList();

  const availableModes: MapMode[] = ['default', ...layers.map(({ id }) => id)];

  const mapMode: MapMode =
    params[MAP_MODE_PARAM_KEY] &&
    availableModes.includes(params[MAP_MODE_PARAM_KEY] as MapMode)
      ? (params[MAP_MODE_PARAM_KEY] as MapMode)
      : 'default';

  const isTimelineMode = TIMELINE_LAYER_IDS.some((id) => id === mapMode);

  const setMapMode = useCallback(
    (mode: MapMode, options?: SetMapModeOptions) => {
      const newParams = new URLSearchParams(location.search);
      options?.removeParams?.forEach((key) => newParams.delete(key));

      if (mode !== 'default') {
        newParams.set(MAP_MODE_PARAM_KEY, mode);

        if (isTimelineMode) {
          // Switching layers shouldn't add a history entry
          history.replace({
            pathname: location.pathname,
            search: `?${newParams.toString()}`,
          });
          return;
        }

        // Navigate to map root (discard any dropped pin)
        history.push({
          pathname: '/',
          search: `?${newParams.toString()}`,
        });
      } else {
        // Remove mapMode param, stay on current path
        newParams.delete(MAP_MODE_PARAM_KEY);
        const search = newParams.toString();
        history.push({
          pathname: location.pathname,
          search: search ? `?${search}` : '',
        });
      }
    },
    [history, isTimelineMode, location.pathname, location.search],
  );

  return {
    mapMode,
    isTimelineMode,
    setMapMode,
  };
};
